import * as React from 'react';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import { createSelector } from 'reselect';
import filter from 'lodash/filter';

import { MatchListStoreModel, ExtendedFilteredMarkets, FilteredMainMarkets } from '../match-list/utils/match-list-models';
import OddsButtonWrapper from './odds-button-wrapper';
import BaseOddsButton from '@om-fe/core/components/odds-button/base-odds-button';
import { marketsIdsToDisplayOddsParamBetweenOdds } from '../match-list/utils/match-list.constants';
import getSlice from '@om-fe/core/utils/get-slice';
import { ModelTypes } from '@om-fe/models';
import { LabelTag } from '@om-fe/widgets/dist/components/label-tag/label-tag';

export interface BuildButtonsPublicProps {
    readonly sliceKey: string;
    readonly eventId: string;
    readonly bettingTypeId?: string;
    readonly eventPartId?: string;
    readonly numberOfOutcomes: number;
    readonly elemIndex: number;
    readonly elemToBeAdded: React.ReactNode;
    readonly name?: string;
    readonly groupId?: string;
    readonly showOddsBar?: boolean;
    readonly modelType: ModelTypes;
    readonly sendHasLabel: (hasLabelResponse: string | undefined) => void;
}

export interface BuildButtonsStoreProps {
    readonly market?: ExtendedFilteredMarkets;
}

export type BuildButtonsProps = BuildButtonsPublicProps & BuildButtonsStoreProps;

class BuildButtonsComponent extends React.PureComponent<BuildButtonsProps> {
    addButtons(list: React.ReactNode[], index: number): React.ReactNode[] {
        const { elemToBeAdded, bettingTypeId, elemIndex, showOddsBar } = this.props;
        const elem: React.ReactNode =
            bettingTypeId && marketsIdsToDisplayOddsParamBetweenOdds.includes(bettingTypeId) && elemIndex === index
                ? elemToBeAdded
                : null;

        return [
            ...list,
            elem,
            <BaseOddsButton
                eventId={this.props.eventId}
                key={index}
                showOddsBar={showOddsBar}
            />,
        ];
    }

    componentDidMount() {
        this.props.sendHasLabel(this.props.market?.labelName && `${this.props.market.labelName}`);
    }

    render() {
        const { market, sliceKey, numberOfOutcomes, eventId, showOddsBar, modelType } = this.props;
        const isOutright: boolean = modelType === ModelTypes.TOURNAMENT ? true : false;
        const outcomeList: Array<string | undefined> | undefined = market && (
            isOutright ? market.outcomeList.slice(0, 3) : market.outcomeList
        );
        const bettingOfferList: Array<string | undefined> | undefined = market && market.bettingOfferList;

        if (!market || !outcomeList || !bettingOfferList) {
            let noOddsButtonsList: React.ReactNode[] = [];

            for (let i: number = 0; i < numberOfOutcomes; i++) {
                noOddsButtonsList = this.addButtons(noOddsButtonsList, i);
            }

            return noOddsButtonsList;
        }

        return (
            <>
                <LabelTag labelName={market?.labelName} labelStyle={market?.labelStyle} />
                {!!market.minSelections && (
                    <span className='MinSelections'>{market.minSelections}</span>
                )}
                {outcomeList.map((outcome: string | undefined, index: number) => (
                    <OddsButtonWrapper
                        key={outcome}
                        sliceKey={sliceKey}
                        outcomeId={outcome}
                        bettingOfferId={bettingOfferList[index]}
                        bettingOfferList={bettingOfferList}
                        eventId={eventId}
                        showOddsBar={showOddsBar}
                    />
                ))}
            </>
        );
    }
}

interface BuildButtonSelectorInterface {
    readonly marketsWithOutcomesAndBettingOffers: Record<string, ExtendedFilteredMarkets>;
    readonly eventId: string;
    readonly bettingTypeId: string;
    readonly eventPartId: string;
    readonly groupId?: string;
}

const createSelectorFunction = createSelector(
    getMarket,
    (arg: BuildButtonSelectorInterface) => {
        const { marketsWithOutcomesAndBettingOffers, eventId, eventPartId, bettingTypeId, groupId } = arg;
        
        if (groupId) {
            return filter(marketsWithOutcomesAndBettingOffers, (el: ExtendedFilteredMarkets) => 
                el.eventId === eventId && (el.groupIds?.indexOf(groupId) !== -1),
            );
        }
        return filter(marketsWithOutcomesAndBettingOffers, (el: ExtendedFilteredMarkets) =>
            el.eventId === eventId && el.bettingTypeId === bettingTypeId && el.eventPartId === eventPartId,
        );
    },
);

function getMarket(arg: BuildButtonSelectorInterface): BuildButtonSelectorInterface {
    const { marketsWithOutcomesAndBettingOffers, eventId, eventPartId, bettingTypeId, groupId } = arg;

    return {
        marketsWithOutcomesAndBettingOffers,
        eventId,
        eventPartId,
        bettingTypeId,
        groupId,
    };
}

const BuildButtons: React.ComponentType<BuildButtonsPublicProps> = connect<BuildButtonsStoreProps>(
    (state: AppState, props: BuildButtonsPublicProps): BuildButtonsStoreProps => {
        const { sliceKey, eventId, bettingTypeId, eventPartId, groupId } = props;
        const slice: MatchListStoreModel | undefined = getSlice<MatchListStoreModel>(state, sliceKey);

        if (!slice || !bettingTypeId || !eventId || !eventPartId) {
            return {};
        }
        
        const selector: ExtendedFilteredMarkets | undefined = createSelectorFunction(
            {
                marketsWithOutcomesAndBettingOffers: slice.marketsWithOutcomesAndBettingOffers,
                eventId,
                bettingTypeId,
                eventPartId,
                groupId,
            })[0];

        return {
            market: selector,
        };
    },
)(BuildButtonsComponent);

export default BuildButtons;
